import React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes as RouterRoutes,
  useNavigate,
} from "react-router-dom";

import withRouter from "withRouter";
import { setNavigator } from "navigation";
import App from "containers/App";
import FourOhFourPage from "containers/FourOhFourPage";

// Route components — static ESM imports (formerly synchronous require().default).
// The route tree loads eagerly, so static top-level imports preserve exact behavior.
import ChannelsPage from "./containers/ChannelsPage";
import ChannelList from "./containers/ChannelsPage/ChannelList";
import NewChannel from "./containers/ChannelsPage/NewChannel";
import EditChannel from "./containers/ChannelsPage/EditChannel";
import AccountPage from "./containers/AccountPage";
import TermsRedirect from "./containers/TermsRedirect";
import LeadsPage from "./containers/LeadsPage";
import LeadsList from "./containers/LeadsPage/LeadsList/index";
import SignInPage from "./containers/SignInPage";
import SelfSignUp from "./containers/SelfSignUp";
import ForgotPasswordPage from "./containers/ForgotPasswordPage";
import ConfirmEmailPage from "./containers/ConfirmEmailPage";
import MoveView from "./containers/MoveView/MoveView";
import GalleryPage from "./containers/GalleryPage/index";
import DownloadsPage from "./containers/DownloadsPage";
import MoveRedirect from "./containers/MoveView/MoveRedirect";
import LegacyGifRedirect from "./containers/LegacyRedirects/LegacyGifRedirect";
import LegacyChannelRedirect from "./containers/LegacyRedirects/LegacyChannelRedirect";
import AdminPage from "./containers/AdminPage";
import AdminDashboard from "./containers/AdminPage/AdminDashboard";
import UsersList from "./containers/AdminPage/Users/UsersList";
import UserDetail from "./containers/AdminPage/Users/UserDetail";
import NewUser from "./containers/AdminPage/Users/NewUser";
import GifMonitor from "./containers/AdminPage/Gifs/GifMonitor";
import GifDetail from "./containers/AdminPage/Gifs/GifDetail";
import DeviceMonitor from "./containers/AdminPage/Devices/DeviceMonitor";
import DeviceDetail from "./containers/AdminPage/Devices/DeviceDetail";
import SettingsEditor from "./containers/AdminPage/Settings/SettingsEditor";
import GrowthDashboard from "./containers/AdminPage/Growth/GrowthDashboard";
import GlobalShares from "./containers/AdminPage/Growth/GlobalShares";

/*
 * Route config.
 *
 * react-router v7 notes for anyone editing this table:
 *
 *  - There is no `exact` prop any more: a path matches exactly by default.
 *    A prefix match is opted into with a trailing `/*` splat.
 *  - Child `path`s are RELATIVE to their parent, and `index: true` is the
 *    child that renders at exactly the parent's path. Parents are layout
 *    routes and render their children through <Outlet/>.
 *  - Ordering is no longer what disambiguates overlapping paths. v5's <Switch>
 *    took the first match in document order; v7's <Routes> takes the highest
 *    ranked match, where a static segment outranks a dynamic one. Every
 *    overlap in this table resolves the same way under both rules — see
 *    src/tests/routeRanking.test.jsx, which pins that equivalence so a future
 *    edit cannot quietly regress it.
 */

const authedRoutes = [
  { path: "/", redirectTo: "/channels" },
  {
    path: "/channels",
    component: ChannelsPage,
    children: [
      { index: true, component: ChannelList },
      { path: "new", component: NewChannel },
      { path: ":channelId/edit", component: EditChannel },
    ],
  },
  { path: "/account", component: AccountPage },
  { path: "/terms", component: TermsRedirect },
  {
    path: "/leads",
    component: LeadsPage,
    children: [
      { index: true, component: LeadsList },
      { path: ":channelId", component: LeadsList },
    ],
  },
  // Gated superadmin section. AdminPage applies the role + feature-flag guard
  // before rendering <Outlet/>, so a non-super_admin (or a user without the
  // react_superadmin_enabled flag) is redirected to /channels and no nested
  // admin route ever renders.
  {
    path: "/admin",
    component: AdminPage,
    children: [
      { index: true, component: AdminDashboard },
      // `users/new` outranks `users/:id` on the static segment, so the literal
      // still wins — under v5 that depended on declaration order.
      { path: "users/new", component: NewUser },
      { path: "users/:id", component: UserDetail },
      { path: "users", component: UsersList },
      { path: "gifs/:id", component: GifDetail },
      { path: "gifs", component: GifMonitor },
      { path: "devices/:id", component: DeviceDetail },
      { path: "devices", component: DeviceMonitor },
      { path: "settings", component: SettingsEditor },
      { path: "growth", component: GrowthDashboard },
      { path: "shares", component: GlobalShares },
    ],
  },
];

const unauthedRoutes = [
  { path: "/terms", component: TermsRedirect },
  { path: "/", redirectTo: "/sign-in" },
  { path: "/sign-in", component: SignInPage },
  { path: "/sign-up", component: SelfSignUp },
  // Was `exact: false` under v5 — the trailing splat is the v7 spelling of a
  // prefix match. An empty splat still matches the bare path.
  { path: "/api/users/confirmation/:token/*", component: ConfirmEmailPage },
];

// Public routes accessible regardless of authentication state.
// Kept separate from unauthedRoutes so that the authenticated redirect
// to /channels does not override gallery pages.
const publicRoutes = [
  // Public so a signed-in user can reach it from the account page's "Forgot your password?" link.
  { path: "/forgot-password", component: ForgotPasswordPage },
  { path: "/ch/:slug/:gifId", component: MoveView },
  { path: "/ch/:slug", component: GalleryPage },
  { path: "/moves/:id", component: MoveRedirect },
  // Public download landing page (emailed link target). Outranks the legacy
  // `/:slug/:gifId` catch-all below on its static first segment.
  { path: "/downloads/:token", component: DownloadsPage },
  // Legacy gallery redirects (without the /ch/ prefix). These are the lowest
  // ranked routes in the table — every `/ch/...`, `/downloads/...` and named
  // app path outranks them, and LegacyChannelRedirect additionally consults
  // APP_ROUTES so a retired app prefix is not treated as a channel slug.
  { path: "/:slug/:gifId", component: LegacyGifRedirect },
  { path: "/:slug", component: LegacyChannelRedirect },
];

// Renders one route-config entry (and its children) as v7 <Route> elements.
const renderRoute = (r, key) => {
  if (r.redirectTo) {
    // v5's <Redirect> replaced the history entry; v7's <Navigate> pushes by
    // default. `replace` keeps the back button working.
    return (
      <Route
        key={key}
        path={r.path}
        element={<Navigate to={r.redirectTo} replace />}
      />
    );
  }

  const Component = r.component;
  const element = <Component />;

  if (r.index) {
    return <Route key={key} index element={element} />;
  }

  return (
    <Route key={key} path={r.path} element={element}>
      {r.children && r.children.map((child, i) => renderRoute(child, i))}
    </Route>
  );
};

/*
 * Registers react-router's navigate function on the navigation bridge so
 * sagas can redirect from outside the React tree. Replaces the
 * connected-react-router `put(push(...))` mechanism.
 */
const NavigationBridge = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    setNavigator(navigate);
    return () => setNavigator(null);
  }, [navigate]);

  return null;
};

const AppWithRouter = withRouter(
  class extends React.PureComponent {
    componentDidUpdate(prevProps) {
      if (this.props.location !== prevProps.location) {
        return (document.querySelector("body").scrollTop = 0);
      }
    }

    render() {
      return <App {...this.props} />;
    }
  }
);

// Paths an authenticated user already has a real route for. Under v5 these
// were shadowed by <Switch>'s first-match rule; v7 ranks rather than orders,
// so the overlap is excluded explicitly instead of relying on a tie-break.
const authedPaths = new Set(authedRoutes.map((r) => r.path));

const Routes = () => (
  <BrowserRouter>
    <NavigationBridge />
    <AppWithRouter
      switchComponent={({ authenticated: authed }) => (
        <RouterRoutes>
          {/* Routes determined by authentication state */}
          {(authed ? authedRoutes : unauthedRoutes).map((r, i) =>
            renderRoute(r, i)
          )}

          {/* Public routes (galleries, legacy redirects) — always accessible */}
          {publicRoutes.map((r, i) => renderRoute(r, `public-${i}`))}

          {/* Redirect remaining unauthed paths (sign-in, etc.) to /channels when logged in */}
          {authed &&
            unauthedRoutes
              .filter((r) => !authedPaths.has(r.path))
              .map((r, i) =>
                renderRoute(
                  { path: r.path, redirectTo: "/channels" },
                  `authed-redirect-${i}`
                )
              )}

          <Route path="*" element={<FourOhFourPage />} />
        </RouterRoutes>
      )}
    />
  </BrowserRouter>
);

export default Routes;
